/**
 * cart-stock-check.js
 * Beside Group — Shopify Cart Stock Validation
 *
 * Upload to: Shopify Admin → Online Store → Themes → Assets
 * Loaded via: cart-stock-check.liquid snippet (included in cart.liquid)
 *
 * What it does:
 * 1. On cart page load, reads cart items
 * 2. Calls sourcing engine stock-check proxy (2s timeout)
 * 3. If ERP down → proceeds silently
 * 4. If items exceed sellable qty → reduces qty or removes from cart
 * 5. Shows toast notification for each change
 * 6. Intercepts checkout button to re-run check on click
 */

(function () {
  "use strict";

  // ── Config (injected by Liquid) ────────────────────────────────────────
  var STOCK_CHECK_URL = window.BSG_STOCK_CHECK_URL || "";
  var STOCK_CHECK_KEY = window.BSG_STOCK_CHECK_KEY || "";
  var COUNTRY         = window.BSG_COUNTRY         || "ae";
  var TIMEOUT_MS      = 2000;

  if (!STOCK_CHECK_URL) {
    console.warn("[BSG] stock-check: BSG_STOCK_CHECK_URL not set");
    return;
  }

  // ── Toast ──────────────────────────────────────────────────────────────
  function injectToastStyles() {
    if (document.getElementById("bsg-toast-styles")) return;
    var style = document.createElement("style");
    style.id = "bsg-toast-styles";
    style.textContent = [
      "#bsg-toast-container {",
      "  position: fixed; top: 20px; right: 20px; z-index: 99999;",
      "  display: flex; flex-direction: column; gap: 10px;",
      "  max-width: 380px; pointer-events: none;",
      "}",
      ".bsg-toast {",
      "  background: #1a1a1a; color: #fff;",
      "  padding: 14px 18px 14px 16px; border-radius: 8px;",
      "  font-size: 14px; line-height: 1.5;",
      "  box-shadow: 0 4px 20px rgba(0,0,0,.35);",
      "  border-left: 4px solid #f59e0b;",
      "  opacity: 0; transform: translateX(20px);",
      "  transition: all .3s ease;",
      "  pointer-events: auto;",
      "  position: relative;",
      "}",
      ".bsg-toast.bsg-toast--error { border-left-color: #ef4444; }",
      ".bsg-toast.bsg-toast--info  { border-left-color: #3b82f6; }",
      ".bsg-toast.bsg-toast--show  { opacity: 1; transform: translateX(0); }",
      ".bsg-toast__close {",
      "  position: absolute; top: 8px; right: 10px;",
      "  background: none; border: none; color: rgba(255,255,255,.6);",
      "  font-size: 18px; cursor: pointer; line-height: 1;",
      "  padding: 0 4px;",
      "}",
      ".bsg-toast__close:hover { color: #fff; }",
      ".bsg-toast__body { padding-right: 20px; }",
    ].join("\n");
    document.head.appendChild(style);
  }

  function showToast(message, type, duration) {
    injectToastStyles();
    type     = type     || "warning";
    duration = duration || 30000;

    var container = document.getElementById("bsg-toast-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "bsg-toast-container";
      document.body.appendChild(container);
    }

    var toast = document.createElement("div");
    toast.className = "bsg-toast bsg-toast--" + type;
    toast.innerHTML = '<div class="bsg-toast__body">' + message + '</div>'
      + '<button class="bsg-toast__close" aria-label="Dismiss">&times;</button>';
    container.appendChild(toast);

    function dismissToast() {
      toast.classList.remove("bsg-toast--show");
      setTimeout(function () { toast.parentNode && toast.parentNode.removeChild(toast); }, 300);
    }

    toast.querySelector(".bsg-toast__close").addEventListener("click", dismissToast);

    setTimeout(function () { toast.classList.add("bsg-toast--show"); }, 10);
    var autoHide = setTimeout(dismissToast, duration);
    toast.addEventListener("mouseenter", function () { clearTimeout(autoHide); });
    toast.addEventListener("mouseleave", function () { autoHide = setTimeout(dismissToast, 5000); });
  }

  // ── Fetch cart ─────────────────────────────────────────────────────────
  function getCart() {
    return fetch("/cart.js")
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  // ── Call stock-check proxy ─────────────────────────────────────────────
  function checkStock(barcodes) {
    var url = STOCK_CHECK_URL +
      "?barcodes=" + encodeURIComponent(barcodes.join(",")) +
      "&country="  + encodeURIComponent(COUNTRY) +
      "&key="      + encodeURIComponent(STOCK_CHECK_KEY);

    var controller, timeoutId;
    var fetchPromise;

    if (window.AbortController) {
      controller = new AbortController();
      timeoutId  = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
      fetchPromise = fetch(url, { signal: controller.signal });
    } else {
      fetchPromise = fetch(url);
      timeoutId    = setTimeout(function () {}, TIMEOUT_MS);
    }

    return fetchPromise
      .then(function (r) {
        clearTimeout(timeoutId);
        return r.json();
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        console.warn("[BSG] stock-check failed or timed out:", err && err.message);
        return { ok: true, fallback: true, items: [] };
      });
  }

  // ── Update cart line qty ───────────────────────────────────────────────
  function updateCartLine(lineNumber, quantity) {
    return fetch("/cart/change.js", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ line: lineNumber, quantity: quantity }),
    }).then(function (r) {
      if (!r.ok) throw new Error("cart/change.js failed: " + r.status);
      return r.json();
    });
  }

  // ── Main: validate and fix cart ────────────────────────────────────────
  var _checking = false;
  var _checkingPromise = null;

  function validateCart(onDone) {
    // If already checking (e.g. page load check still running), wait for it
    if (_checking && _checkingPromise) {
      _checkingPromise.then(function(canProceed) {
        if (onDone) onDone(canProceed !== false);
      });
      return;
    }
    _checking = true;
    var resolveCheck;
    _checkingPromise = new Promise(function(resolve) { resolveCheck = resolve; });

    getCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) {
        _checking = false;
        if (onDone) onDone(true);
        return;
      }

      // Map variant_id → { title, sku, quantity, line_index }
      var lineMap = {};
      var barcodes = [];
      cart.items.forEach(function (item, idx) {
        var sku = item.sku || "";
        if (!sku) return;
        lineMap[sku] = {
          title:      item.product_title || item.title,
          variant:    item.variant_title || "",
          variantId:  item.variant_id,
          lineKey:    item.key || String(item.variant_id),
          lineNumber: idx + 1, // 1-based line index — most reliable for /cart/change.js
          cartQty:    item.quantity,
        };
        barcodes.push(sku);
      });

      if (!barcodes.length) {
        _checking = false; if (resolveCheck) resolveCheck(true);
        if (onDone) onDone(true);
        return;
      }

      checkStock(barcodes).then(function (data) {
        if (!data || data.fallback) {
          console.log("[BSG] stock-check: ERP unavailable, proceeding");
          _checking = false; if (resolveCheck) resolveCheck(true);
          if (onDone) onDone(true);
          return;
        }
        if (!data.items || !data.items.length) {
          _checking = false; if (resolveCheck) resolveCheck(true);
          if (onDone) onDone(true);
          return;
        }

        // Build sellable map
        var sellableMap = {};
        (data.items || []).forEach(function (item) {
          sellableMap[item.barcode] = item.sellable; // null = ERP didn't return it
        });

        // Find items needing adjustment
        var updates   = [];
        var anyChange = false;

        barcodes.forEach(function (barcode) {
          var line     = lineMap[barcode];
          var sellable = sellableMap[barcode];
          if (!line) return;
          if (sellable === null || sellable === undefined) return; // ERP didn't return — skip

          if (sellable <= 0) {
            // No stock — remove from cart
            updates.push({ lineNumber: line.lineNumber, quantity: 0, line: line, sellable: 0 });
            anyChange = true;
          } else if (line.cartQty > sellable) {
            // Reduce qty
            updates.push({ lineNumber: line.lineNumber, quantity: sellable, line: line, sellable: sellable });
            anyChange = true;
          }
        });

        if (!anyChange) {
          _checking = false; if (resolveCheck) resolveCheck(true);
          if (onDone) onDone(true);
          return;
        }

        // Apply updates sequentially
        // Use /cart/update.js with variant_id keys — atomic, no line shifting
        var chain = getCart().then(function(freshCart) {
          var updates_payload = {};
          (freshCart && freshCart.items || []).forEach(function(item) {
            updates.forEach(function(upd) {
              if (item.variant_id === upd.line.variantId) {
                updates_payload[String(item.variant_id)] = upd.quantity;
              }
            });
          });
          console.log('[BSG] cart/update.js payload:', JSON.stringify(updates_payload));
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates_payload })
          }).then(function(r) {
            if (!r.ok) throw new Error('cart/update.js failed: ' + r.status);
            return r.json();
          });
        });

        // Show toasts immediately — before cart update so user sees them
        updates.forEach(function (upd) {
          var itemName = upd.line.title + (upd.line.variant ? " — " + upd.line.variant : "");
          if (upd.sellable <= 0) {
            showToast(
              "<strong>" + itemName + "</strong><br>" +
              "This item is no longer available and has been removed from your cart.",
              "error"
            );
          } else {
            showToast(
              "<strong>" + itemName + "</strong><br>" +
              "Only " + upd.sellable + " unit" + (upd.sellable > 1 ? "s" : "") +
              " available. Quantity updated.",
              "warning"
            );
          }
        });

        chain.then(function () {
          // Delay reload 3s so user can read the toast
          setTimeout(function () {
            _checking = false; if (resolveCheck) resolveCheck(false);
            sessionStorage.setItem("bsg_just_adjusted", "1");
            if (onDone) {
              onDone(false);
            } else {
              window.location.reload();
            }
          }, 2000);

        }).catch(function (err) {
          console.error("[BSG] cart update failed:", err);
          _checking = false;
          if (onDone) onDone(true);
        });

      });
    }).catch(function () {
      _checking = false; if (resolveCheck) resolveCheck(true);
      if (onDone) onDone(true);
    });
  }

  // ── Intercept checkout button ──────────────────────────────────────────
  function interceptCheckout() {
    var selectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href="/checkout"]',
      ".cart__checkout",
      "#checkout",
    ];

    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (btn) {
        if (btn._bsgIntercepted) return;
        btn._bsgIntercepted = true;

        btn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();

          var el = this;
          el.disabled = true;
          var orig = el.textContent || el.value || "Checkout";
          if (el.textContent) el.textContent = "Checking availability…";
          if (el.value)       el.value       = "Checking availability…";

          validateCart(function (canProceed) {
            el.disabled = false;
            if (el.textContent) el.textContent = orig;
            if (el.value)       el.value       = orig;

            if (canProceed) {
              // Navigate to checkout directly
              window.location.href = '/checkout';
            }
            // else: cart was modified, page reloading
          });
        }, true);
      });
    });
  }

  // ── Init ───────────────────────────────────────────────────────────────
  function init() {
    // Check if we just did an adjustment this session to avoid infinite reload
    var justAdjusted = sessionStorage.getItem("bsg_just_adjusted");
    if (justAdjusted) {
      sessionStorage.removeItem("bsg_just_adjusted");
      console.log("[BSG] Cart was just adjusted — skipping recheck");
    } else {
      // Run stock check on page load (silent — only updates if needed)
      validateCart(null);
    }
    // Always intercept checkout buttons
    interceptCheckout();
    // Re-intercept after Shopify AJAX cart updates
    document.addEventListener("cart:updated", interceptCheckout);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

